import React from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowColorCard from './components/WorkflowColorCard';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';

const basicsSteps = [
  {
    label: 'Part1',
    title: 'Kiroとは: AIとの対話の基本原則',
    path: '/devdocs/workflow/kiro-basics-1',
  },
  {
    label: 'Part2',
    title: 'Steeringファイル: AIにルールを伝える方法',
    path: '/devdocs/workflow/kiro-basics-2',
    active: true,
  },
  {
    label: 'Part3',
    title: '機能と運用: 知っておくべきKiroの機能',
    path: '/devdocs/workflow/kiro-basics-3',
  },
  {
    label: 'Part4',
    title: 'ワークフロー全体像: 本プロジェクトの進め方',
    path: '/devdocs/workflow/kiro-basics-4',
  },
];

const KIRO_GENERAL_DIRS = [
  {
    name: 'steering',
    desc: 'AIのルールファイル（Steeringファイル）',
    color: '#1565c0',
    children: [{ name: '*.md', desc: 'プロジェクト固有のルール・制約・手順' }],
  },
  {
    name: 'specs',
    desc: 'Specsモードの仕様策定ファイル（自動生成）',
    color: '#2e7d32',
    children: [{ name: '{spec名}/', desc: 'requirements.md / design.md / tasks.md' }],
  },
  {
    name: 'hooks',
    desc: 'Agent Hooks定義（JSON）',
    color: '#f9a825',
    children: [{ name: '*.json', desc: 'イベント駆動の自動化ルール' }],
  },
  {
    name: 'settings',
    desc: 'Kiro設定',
    color: '#546e7a',
    children: [{ name: 'mcp.json', desc: 'MCP（Model Context Protocol）サーバー設定' }],
  },
];

const KIRO_PJ_DIRS = [
  {
    name: 'steering',
    desc: 'ルールファイル（標準）',
    color: '#1565c0',
    children: undefined,
  },
  {
    name: 'specs',
    desc: 'API開発の作業ディレクトリ（本PJ独自の用途で利用）',
    color: '#2e7d32',
    children: undefined,
  },
  {
    name: 'hooks',
    desc: 'Agent Hooks（標準、本PJでは未使用）',
    color: '#f9a825',
    children: undefined,
  },
  {
    name: 'requirements',
    desc: '要件定義ドキュメント（本PJ独自）',
    color: '#e65100',
    children: undefined,
  },
  {
    name: 'e2e_test',
    desc: 'E2Eテスト定義・結果（本PJ独自）',
    color: '#6a1b9a',
    children: undefined,
  },
  {
    name: 'docs',
    desc: 'ワークフロー関連ドキュメント（本PJ独自）',
    color: '#546e7a',
    children: undefined,
  },
];
const WorkflowKiroBasics2: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="Kiro基本編 Part2 — Steeringファイル: AIにルールを伝える方法"
      seriesName="Kiro基本編"
      tags={['Steering', 'fileMatch', 'Rules']}
      seriesSteps={basicsSteps}
      prevLink={{ label: 'Part1: Kiroとは', path: '/devdocs/workflow/kiro-basics-1' }}
      nextLink={{ label: 'Part3: 機能と運用', path: '/devdocs/workflow/kiro-basics-3' }}
    >
      {/* この記事で学べること */}
      <WorkflowLearningPoints
        items={[
          '.kiro ディレクトリの構造と各ファイルの役割',
          'Steeringファイルの種類（標準 / プロジェクト固有）',
          'Steeringファイルの読み込みモード（always / fileMatch / manual）',
          '分割粒度の設計方法',
          'Steeringファイル間の関連図',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '.kiro ディレクトリの構造' },
          { id: 'section-2', label: 'Steeringファイルの種類' },
          { id: 'section-3', label: 'Steeringファイルの仕組み' },
          { id: 'section-4', label: 'Steeringファイルの関連図' },
          { id: 'section-5', label: 'ベストプラクティスとアンチパターン' },
        ]}
      />

      {/* 1. .kiro ディレクトリの構造 */}
      <WorkflowSection title=".kiro ディレクトリの構造" id="section-1" number={1}>
        <WorkflowText>
          プロジェクトルートに配置する .kiro
          ディレクトリが、AIの「脳」にあたります。ここに配置したファイルの内容をAIが読み込み、プロジェクト固有のルールや知識として活用します。
        </WorkflowText>

        <WorkflowSection title="一般的な .kiro ディレクトリ構造" subNumber="1.1">
          <WorkflowDirectoryTree root=".kiro/" entries={KIRO_GENERAL_DIRS} />
          <WorkflowTable
            headers={['ディレクトリ', '役割', '必須/任意']}
            rows={[
              ['steering/', 'AIの振る舞いを制御するルールファイルの格納場所', '必須'],
              ['specs/', 'Specsモードで生成された仕様策定ファイルの格納場所', '任意'],
              ['hooks/', 'イベント駆動の自動化ルール（JSON形式）', '任意'],
              ['settings/', 'MCP等のKiro設定', '任意'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="ユーザーレベルの .kiro ディレクトリ" subNumber="1.2">
          <WorkflowText>
            プロジェクト配下だけでなく、ホームディレクトリにもKiroの設定を配置できます。ワークスペース（プロジェクト配下）とユーザーレベルの両方に同名ファイルがある場合はワークスペースが優先されます。
          </WorkflowText>
          <WorkflowCodeBlock
            language="text"
            code={`~/.kiro/
├── steering/          # 全プロジェクト共通のSteeringファイル
│   └── *.md               # 個人のコーディング規約等
└── settings/
    └── mcp.json           # ユーザーレベルのMCP設定`}
          />
        </WorkflowSection>

        <WorkflowSection title="本プロジェクトでの拡張" subNumber="1.3">
          <WorkflowText>
            本プロジェクトでは標準構造に加えて、ワークフローに必要な作業ディレクトリを .kiro
            配下に追加しています。
          </WorkflowText>
          <WorkflowDirectoryTree root=".kiro/" entries={KIRO_PJ_DIRS} />
          <WorkflowInfoBox>
            .kiro ディレクトリの中に自由にサブディレクトリを追加して構いません。Kiroが認識するのは
            steering/、specs/、hooks/、settings/
            のみで、それ以外はプロジェクト固有の用途で自由に利用できます。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 2. Steeringファイルの種類 */}
      <WorkflowSection title="Steeringファイルの種類" id="section-2" number={2}>
        <WorkflowText>
          Steeringファイルは .kiro/steering/
          に配置するMarkdownファイルで、AIが作業する際のルール・制約・手順を定義します。
        </WorkflowText>

        <WorkflowSection
          title="標準Steeringファイル（どのプロジェクトでも必ず作成する）"
          subNumber="2.1"
        >
          <WorkflowTable
            headers={['ファイル名', '役割', '概要']}
            rows={[
              [
                <Stack key="product" direction="row" alignItems="center" spacing={0.5}>
                  <Chip
                    label="必須"
                    size="small"
                    color="error"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                  <Typography variant="body2">product.md</Typography>
                </Stack>,
                'プロダクト要件定義',
                'サブシステム構成、機能一覧、ビジネスルール、非機能要件',
              ],
              [
                <Stack key="structure" direction="row" alignItems="center" spacing={0.5}>
                  <Chip
                    label="必須"
                    size="small"
                    color="error"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                  <Typography variant="body2">structure.md</Typography>
                </Stack>,
                'プロジェクト構造',
                'ディレクトリ構成、パッケージ構成、クラス命名規則、配置ルール',
              ],
              [
                <Stack key="tech" direction="row" alignItems="center" spacing={0.5}>
                  <Chip
                    label="必須"
                    size="small"
                    color="error"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                  <Typography variant="body2">tech.md</Typography>
                </Stack>,
                '技術スタック・規約',
                '技術選定、アーキテクチャ、コーディング規約、フレームワーク固有ルール',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="プロジェクト固有Steeringファイル（自由に追加する）" subNumber="2.2">
          <WorkflowText>
            以下はプロジェクトごとに自由に作成するSteeringファイルです。本プロジェクトでは以下のファイルを作成しました。
          </WorkflowText>
          <WorkflowTable
            headers={['ファイル名', '役割', '概要']}
            rows={[
              [
                'note_rule.md',
                'Rv指摘・注釈ルール',
                'レビュー指摘のフォーマット定義（rv/info/warn/alert）',
              ],
              [
                'create_proposal.md',
                'proposal.md生成ルール',
                'RFP対話→技術仕様の決定事項を構造化して出力',
              ],
              [
                'create_business.md',
                'business.md生成ルール',
                'ユーザー視点のビジネスユースケースを出力',
              ],
              [
                'create_usecases.md',
                'usecases.md生成ルール',
                'proposal+businessからシステムユースケースを出力',
              ],
              [
                'api_project_rule.md',
                'API開発ワークフロー',
                '設計→コード生成→テストの3フェーズ全体定義',
              ],
              [
                'api_specs_rule.md',
                'API仕様書ルール',
                'API仕様書の記載ルール、権限制御の連動ルール',
              ],
              ['create_dd.md', 'DD書作成ルール', 'DD書のフォーマット、各レイヤーの記載ルール'],
              [
                'create_schema.md',
                'スキーマ定義ルール',
                'テーブル定義書の構造、命名規則、Flyway連携',
              ],
              [
                'migration_rule.md',
                'マイグレーションルール',
                'Flywayファイルの命名・SQL記述・権限マイグレーション',
              ],
              ['word_rule.md', '用語集ルール', '物理名の参照・追記ルール'],
              [
                'local_test.md',
                'ローカルテスト手順',
                'Docker環境の構築・アプリ起動・テスト実行手順',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* 3. Steeringファイルの仕組み */}
      <WorkflowSection title="Steeringファイルの仕組み" id="section-3" number={3}>
        <WorkflowText>
          Steeringファイルには読み込みモードがあり、いつAIに読み込まれるかを制御できます。
        </WorkflowText>

        <WorkflowSection title="読み込みモード" subNumber="3.1">
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <WorkflowColorCard
                title="always"
                description="全てのやりとりで常に読み込まれる。デフォルト設定。"
                color="#2e7d32"
                badge="DEFAULT"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <WorkflowColorCard
                title="fileMatch"
                description="特定パターンのファイルを操作するときのみ読み込まれる。"
                color="#f9a825"
                badge="CONDITIONAL"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <WorkflowColorCard
                title="manual"
                description="ユーザーが #ファイル名 で明示的に指定したときのみ読み込まれる。"
                color="#1565c0"
                badge="ON DEMAND"
              />
            </Grid>
          </Grid>
        </WorkflowSection>

        <WorkflowSection title="設定例（Front Matter）" subNumber="3.2">
          <WorkflowCodeBlock
            title="fileMatch 単一指定"
            language="yaml"
            code={`---
inclusion: fileMatch
globs:
  - docs/30_DD/**/*.md
---`}
          />
          <WorkflowCodeBlock
            title="fileMatch 複数指定"
            language="yaml"
            code={`---
inclusion: fileMatch
globs:
  - docs/20_BD/20_API一覧/**/*.md
  - docs/20_BD/30_API仕様書/**/*.md
---`}
          />
        </WorkflowSection>

        <WorkflowSection title="いつどのモードを使うか" subNumber="3.3">
          <WorkflowTable
            headers={['モード', '使いどころ', '本PJでの例']}
            rows={[
              [
                'always',
                'AIが常に知っておくべき情報。プロジェクト全体の規約・構造',
                'product.md, structure.md, tech.md',
              ],
              [
                'fileMatch',
                '特定の成果物を編集するときだけ必要なルール',
                'create_dd.md, create_schema.md',
              ],
              [
                'manual',
                'ワークフロー全体の手順。ユーザーが意図的に起動',
                'api_project_rule.md, local_test.md',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Steeringファイルの分割粒度" subNumber="3.4">
          <WorkflowText>
            Steeringファイルは「修正対象ファイルの種別（役割）ごとに1ファイル」が基本的な分割単位です。
          </WorkflowText>
          <WorkflowTable
            headers={['分割基準', '説明']}
            rows={[
              [
                'fileMatch',
                '成果物の種別（= 保存先ディレクトリ）ごとに1ファイル作成し、そのディレクトリにマッチするglobsを設定する',
              ],
              [
                'manual',
                '複数種別のファイルを横断的に操作するワークフロー手順書。特定のglobsに紐付けられないため手動起動にする',
              ],
              [
                'always',
                'どの作業でも前提となる共通知識。数は最小限に抑える（コンテキスト枠を圧迫するため）',
              ],
            ]}
          />
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            本PJでのfileMatchの実例
          </Typography>
          <WorkflowTable
            headers={['steering', 'globs', '対応する成果物']}
            rows={[
              [
                'create_proposal.md',
                '.kiro/requirements/**/*.rfp.md, .../*.proposal.md',
                'proposal.md',
              ],
              [
                'create_business.md',
                '.kiro/requirements/**/*.rfp.md, .../*.business.md',
                'business.md',
              ],
              [
                'create_usecases.md',
                '.kiro/requirements/**/*.proposal.md, .../*.usecases.md',
                'usecases.md',
              ],
              ['create_schema.md', 'docs/20_BD/10_スキーマ定義/**/*.md', 'スキーマ定義書'],
              ['create_dd.md', 'docs/30_DD/**/*.md', 'DD書（詳細設計書）'],
              [
                'api_specs_rule.md',
                'docs/20_BD/20_API一覧/**/*.md, .../30_API仕様書/**/*.md',
                'API仕様書・API一覧',
              ],
              [
                'migration_rule.md',
                'src/main/resources/db/migration/**/*.sql',
                'FlywayマイグレーションSQL',
              ],
              ['word_rule.md', 'docs/00_共通/用語集.md', '用語集'],
            ]}
          />
          <WorkflowInfoBox>
            api_project_rule.md が manual
            なのは、複数種類のファイル（API仕様書・DD書・コード・マイグレーション）を横断的に操作するワークフローだから。特定のglobsパターンに紐付けると、一部のファイル操作時にしか発動せず意図通りに動かない。
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="補足: ユーザーレベルとワークスペースレベル" subNumber="3.5">
          <WorkflowCodeBlock
            language="text"
            code={`~/.kiro/steering/
└── coding_common.md    ← 全PJ共通のコーディング規約

{PJ}/.kiro/steering/
└── tech.md             ← このPJ固有の技術ルールのみ`}
          />
          <WorkflowInfoBox type="warning">
            同名ファイルが両方に存在する場合、PJ配下のファイルで上書きされます（マージではない）。共通ルールを活かしつつPJ固有を追加したい場合はファイル名を分けてください。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 4. Steeringファイルの関連図 */}
      <WorkflowSection title="Steeringファイルの関連図" id="section-4" number={4}>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                border: 2,
                borderColor: 'primary.main',
                borderRadius: 2,
                p: 1.5,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(25, 118, 210, 0.08)'
                    : 'rgba(25, 118, 210, 0.04)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                width: 'fit-content',
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                api_project_rule.md（ワークフロー全体）
              </Typography>
            </Box>
            <Stack spacing={0.75} sx={{ pl: 3 }}>
              {[
                { name: 'api_specs_rule.md', desc: 'API仕様書ルール' },
                { name: 'create_dd.md', desc: 'DD書作成ルール' },
                { name: 'create_schema.md', desc: 'スキーマ定義ルール' },
                { name: 'migration_rule.md', desc: 'マイグレーションルール' },
                { name: 'word_rule.md', desc: '用語集ルール' },
                { name: 'note_rule.md', desc: 'Rv指摘・注釈ルール' },
                { name: 'local_test.md', desc: 'ローカルテスト手順' },
              ].map((item) => (
                <Stack key={item.name} direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    └── 参照 →
                  </Typography>
                  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.25 }}>
                    <Typography variant="caption" fontWeight={600}>
                      {item.name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {item.desc}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 1 }}>
              <Stack spacing={1}>
                {[
                  { name: 'create_proposal.md', desc: 'RFP対話→技術仕様の決定事項を構造化' },
                  { name: 'create_business.md', desc: 'ユーザー視点の操作シナリオに変換' },
                  {
                    name: 'create_usecases.md',
                    desc: 'proposal+businessからシステムユースケースを生成',
                  },
                ].map((item) => (
                  <Stack key={item.name} direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                        bgcolor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(25, 118, 210, 0.12)'
                            : 'rgba(25, 118, 210, 0.06)',
                      }}
                    >
                      <Typography variant="caption" fontWeight={600}>
                        {item.name}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      — {item.desc}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 1 }}>
              <Stack spacing={1}>
                {[
                  { name: 'product.md', desc: 'AIが機能理解・ビジネスルール確認時に参照' },
                  { name: 'structure.md', desc: 'AIがクラス命名・配置先を決定する際に参照' },
                  { name: 'tech.md', desc: 'AIがコード生成時にコーディングルールとして参照' },
                ].map((item) => (
                  <Stack key={item.name} direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Typography variant="caption" fontWeight={600}>
                        {item.name}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      — {item.desc}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </WorkflowSection>

      {/* 5. ベストプラクティスとアンチパターン */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-5" number={5}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'product.md / structure.md / tech.md を最初に整備する',
              effect: 'この3ファイルがAIの基礎知識になる。品質の土台はここで決まる',
            },
            {
              practice: 'Steeringのalwaysは3〜5ファイルに抑える',
              effect: '常時読み込みはコンテキスト枠を毎回消費する。本当に常時必要なものだけに絞る',
            },
            {
              practice: '同じglobsパターンのルールは1ファイルにまとめる',
              effect: 'AIがルール間の関連を把握しやすくなり、一貫した出力が得られる',
            },
            {
              practice: 'ルールは具体的・明確に記述する',
              effect: '曖昧な表現を排除し、AIの判断ブレを防ぐ',
            },
            {
              practice: '使わなくなったルールは定期的に棚卸しする',
              effect: '不要なルールが残るとコンテキスト消費とAIの混乱を招く',
            },
            {
              practice: '読み込みタイミングが異なるルールは別ファイルに分ける',
              effect: '内容の関連性ではなく発火タイミングで分割を判断する',
            },
          ]}
        />
        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: 'alwaysファイルを10個以上にする',
              effect: '毎回の基礎消費が膨大になりAIの応答品質が下がる',
            },
            {
              practice: 'Steeringに曖昧な表現（「適宜」「必要に応じて」）を多用する',
              effect: 'AIの判断がバラつき、成果物の品質が安定しない',
            },
            {
              practice: 'ルール間で矛盾する記述を放置する',
              effect: 'AIがどちらに従うべきか判断できず出力がぶれる',
            },
            {
              practice: '1ルール1ファイルのように極端に細分化する',
              effect: 'globs設計が複雑になり、片方だけ読み込まれて不整合が起きる',
            },
            {
              practice: '1ファイルにワークフロー手順+ルール+テンプレートを全部詰め込む',
              effect: 'ファイルが巨大化しAIが重要部分を見落とす',
            },
            {
              practice: '使わなくなったSteeringルールを放置する',
              effect: '不要なルールがコンテキストを消費し続け、AIが古いルールに従う',
            },
          ]}
        />
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};

export default WorkflowKiroBasics2;
